import React from 'react';
import {
  getHeroContent, HeroImage, StoreLogo, SearchBar, CartBtn, TabContent, StoreFooter, FaHeart, getTabLabel, InlineEditableText
} from './ThemeLayoutHelpers';

export default function UrbanLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#4b5563";
  const copy = getHeroContent(p, {
    eyebrow: "Urban Essentials",
    title: "Sleek. Functional. Minimal.",
    subtitle: "Modern lifestyle design curated for contemporary spaces and minimal living.",
    primaryCta: "Shop Collection",
    secondaryCta: "View Catalog",
  });

  return (
    <div className="min-h-screen flex flex-col bg-[#fcfcfc] text-gray-900" style={{ fontFamily: T.fontFamily || "Montserrat, sans-serif" }}>
      {/* Minimalistic Header */}
      <nav className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <StoreLogo storeInfo={p.storeInfo} border="border border-gray-900" />
            <div>
              <p className="text-sm font-extrabold tracking-widest text-gray-900 uppercase">{p.storeInfo?.businessName}</p>
              <p className="text-[9px] text-gray-500 font-bold uppercase tracking-wider mt-0.5">{p.storeInfo?.businessCategory || "Lifestyle"}</p>
            </div>
          </div>
          <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="flex-1 hidden md:block max-w-sm" />
          <div className="flex items-center gap-3">
            <button onClick={p.handleGoToWishlist} className="relative p-2 rounded-lg border border-gray-200 hover:bg-gray-50 bg-white text-gray-800 transition">
              <FaHeart className="text-base" />
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 h-4 w-4 text-[9px] rounded-full text-white flex items-center justify-center font-bold bg-gray-850" style={{ backgroundColor: primary }}>
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} />
            {p.userMenuElement}
          </div>
        </div>
      </nav>

      {/* Hero Header */}
      <section className="max-w-7xl mx-auto w-full px-6 pt-6">
        <div className="border border-gray-200 bg-white p-6 md:p-8 rounded-xl shadow-sm grid md:grid-cols-2 gap-8 items-center">
          <div className="space-y-6">
            <div>
              <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-[10px] font-bold uppercase tracking-widest mb-4">
                <InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText>
              </span>
              <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight text-gray-950 leading-tight">
                <InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText>
              </h1>
              <p className="mt-4 text-gray-600 text-sm md:text-base leading-relaxed">
                <InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText>
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => p.setActiveTab("products")}
                className="px-6 py-3 rounded-lg text-xs font-bold text-white transition hover:bg-gray-850"
                style={{ backgroundColor: primary }}
              >
                <InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText>
              </button>
            </div>
          </div>
          <div className="rounded-xl overflow-hidden shadow-md">
            <HeroImage p={p} themeId="urban" className="h-[280px] md:h-[340px]">
              <div className="absolute inset-0 bg-gradient-to-t from-gray-950/30 to-transparent pointer-events-none" />
            </HeroImage>
          </div>
        </div>
      </section>

      {/* Navigation and Tab Layout */}
      <section className="max-w-7xl mx-auto w-full px-6 mt-8">
        <div className="flex gap-4 border-b border-gray-200 overflow-x-auto">
          {[
            ["products", getTabLabel(p, "products")],
            ["services", getTabLabel(p, "services")],
            ["details", getTabLabel(p, "details")]
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => p.setActiveTab(k)}
              className={`pb-4 px-2 text-xs font-extrabold tracking-wider uppercase border-b-2 transition whitespace-nowrap ${
                p.activeTab === k ? 'border-gray-900 text-gray-950' : 'border-transparent text-gray-500 hover:text-gray-900'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </section>

      {/* Tab Contents */}
      <section className="flex-1 max-w-7xl mx-auto w-full px-6 py-6">
        <TabContent p={p} />
      </section>

      <StoreFooter p={p} />
    </div>
  );
}
